import * as Html from "../util/Html.ts";

// Type definitions
interface ScoreBaseObject {
  [key: string]: number | ScoreBaseObject;
}
type ScoreBase = number | ScoreBaseObject;
type ScorerFunction<T> = (object: T, searchText: string) => number;

interface DataCache<T> {
  need(): Promise<T[]>;
}

interface ScoredData<T> {
  data: T;
  score: number;
}

interface SuggestionBean {
  value: string;
  alternates?: string[];
  [key: string]: any;
}

// Character groups for accent-insensitive search
const CHAR_GROUPS: Record<string, string> = {
  a: "aàáâãäå",
  c: "cç",
  e: "eéèëê",
  i: 'iíìïî',
  n: 'nǹńñṅň',
  o: 'oóòõöô',
  u: 'uúùüû'
};

const REGEXP_ESCAPE_CHARS = "\\()[]{}.*+?|^$";

// Export declarations
export {
  SearchController,
  easyScorer,
  applyScorer,
  Searcher,
  createRegexSearcher,
  createTextSearcher,
  createFieldSearcher
};

// ============= Unified Search System =============

/**
 * Creates a regex pattern for accent-insensitive search
 */
function getSearchRegExp(searchText: string, wholeWord: boolean = true): RegExp {
  let result = '';
  for (let i = 0; i < searchText.length; i++) {
    const char = searchText.charAt(i).toLowerCase();
    if (CHAR_GROUPS[char]) {
      result += '[' + CHAR_GROUPS[char] + ']';
    } else if (REGEXP_ESCAPE_CHARS.indexOf(char) > -1) {
      result += '\\' + char;
    } else {
      result += char;
    }
  }
  // Search at word boundaries if wholeWord is true
  const pattern = wholeWord ? "(^|\\W)" + result : result;
  return new RegExp(pattern, "gi");
}

/**
 * Normalizes text for search (lowercase, remove accents)
 */
function normalize(str: string): string {
  return str.trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

/**
 * Removes accents from string (alias for compatibility)
 */
function removeAccent(str: string): string {
  return str.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

/**
 * Text-based searcher using regex (similar to old Searcher class)
 */
class Searcher {
  private wholeWord: boolean;
  private properties?: string[];

  constructor(properties?: string | string[], wholeWord: boolean = true) {
    this.wholeWord = wholeWord;
    if (typeof properties === 'string') {
      this.properties = properties.split(',').map(p => p.trim());
    } else {
      this.properties = properties;
    }
  }

  private getRegExp(searchText: string): RegExp {
    return getSearchRegExp(searchText, this.wholeWord);
  }

  search(searchText: string, text: string): RegExpMatchArray | null {
    const regex = this.getRegExp(searchText);
    return text.match(regex);
  }

  filter(searchText: string, texts: string[]): string[] {
    const regex = this.getRegExp(searchText);
    return texts.filter(text => text.match(regex));
  }

  score(obj: any, searchText: string): number {
    if (!searchText) return 0;

    const regex = this.getRegExp(searchText);
    let maxScore = 0;

    // If properties are defined, search in specific properties
    if (this.properties && typeof obj === 'object') {
      for (const prop of this.properties) {
        const value = this.getPropertyValue(obj, prop);
        if (typeof value === 'string') {
          const matches = value.match(regex);
          if (matches) {
            // Higher score for matches at the beginning
            const position = value.indexOf(matches[0]);
            const score = position === 0 ? 100 : 50 - position;
            maxScore = Math.max(maxScore, score);
          }
        }
      }
    } else if (typeof obj === 'string') {
      const matches = obj.match(regex);
      if (matches) {
        const position = obj.indexOf(matches[0]);
        maxScore = position === 0 ? 100 : 50 - position;
      }
    }

    return maxScore;
  }

  filterSuggestion(searchText: string, suggestions: SuggestionBean[]): SuggestionBean[] {
    const regex = this.getRegExp(searchText);
    const result: SuggestionBean[] = [];

    for (const sugg of suggestions) {
      if (sugg.value.match(regex)) {
        result.push(sugg);
      } else if (sugg.alternates) {
        for (const alt of sugg.alternates) {
          if (alt.match(regex)) {
            result.push(sugg);
            break;
          }
        }
      }
    }

    return result;
  }

  private getPropertyValue(obj: any, path: string): any {
    const parts = path.split('.');
    let current = obj;
    for (const part of parts) {
      if (current == null) return undefined;
      current = current[part];
    }
    return current;
  }
}

// ============= Score-based Search System =============

class SearchController<T> {
  private nodeResult: HTMLElement;
  private searchInput: HTMLInputElement;
  private TPL_result: MUS;
  private TPL_noresult: MUS;
  private datasCache: DataCache<T>;
  private scoreSearch: ScorerFunction<T>;
  private wrapOnInput: () => void;

  constructor(
    nodeResult: HTMLElement,
    searchInput: HTMLInputElement,
    TPL_result: MUS,
    TPL_noresult: MUS,
    datasCache: DataCache<T>,
    scoreSearch: ScorerFunction<T>
  ) {
    this.nodeResult = nodeResult;
    this.searchInput = searchInput;
    this.TPL_result = TPL_result;
    this.TPL_noresult = TPL_noresult;
    this.datasCache = datasCache;
    this.scoreSearch = scoreSearch;

    this.wrapOnInput = () => this.update();

    this.searchInput.addEventListener('input', this.wrapOnInput);
  }

  async update(): Promise<void> {
    const searchText = this.searchInput.value;
    const searchResult = applyScorer(
      await this.datasCache.need(),
      this.scoreSearch,
      searchText
    );

    if (searchResult.length > 0) {
      Html.setHtmlArray(this.nodeResult, this.TPL_result, searchResult);
    } else {
      Html.setHtml(this.nodeResult, this.TPL_noresult, { searchText });
    }
  }

  destroy(): void {
    this.searchInput.removeEventListener('input', this.wrapOnInput);
  }
}

function max(prev: number, next: number): number {
  return next > prev ? next : prev;
}

/**
 * Creates a scorer function for object properties
 */
function easyScorer<T>(data: Record<string, number>): ScorerFunction<T> {
  return function (object: T, searchtext: string): number {
    if (!searchtext) return 1;
    return scoreObject(object, data, searchtext);
  };
}

/**
 * Creates a regex-based scorer that can work with specific properties
 */
function createRegexSearcher<T>(properties?: string | string[]): ScorerFunction<T> {
  const searcher = new Searcher(properties);
  return function (object: T, searchText: string): number {
    return searcher.score(object, searchText);
  };
}

/**
 * Creates a text-based scorer using normalized text matching
 */
function createTextSearcher<T>(...propertyPaths: string[]): ScorerFunction<T> {
  return function (object: T, searchText: string): number {
    if (!searchText) return 1;

    const normalizedSearch = normalize(searchText);
    let maxScore = 0;

    for (const path of propertyPaths) {
      const value = getPropertyByPath(object, path);
      if (typeof value === 'string') {
        const normalizedValue = normalize(value);
        const idx = normalizedValue.indexOf(normalizedSearch);
        if (idx === 0) {
          maxScore = Math.max(maxScore, 100);
        } else if (idx > 0) {
          maxScore = Math.max(maxScore, 50);
        }
      }
    }

    return maxScore;
  };
}

/**
 * Creates a field-based scorer compatible with Searcher.js format
 * Format: "field1:score1,field2:score2"
 */
function createFieldSearcher<T>(fieldSpec: string): ScorerFunction<T> {
  interface FieldParam {
    field: string;
    scoreBase: number;
  }

  const params: FieldParam[] = fieldSpec.split(',').map(p => {
    const [field, scoreStr] = p.split(':');
    return {
      field: field.trim(),
      scoreBase: scoreStr ? parseInt(scoreStr, 10) : 1
    };
  });

  return function (object: T, searchText: string): number {
    if (!searchText) return 0;

    const normalizedSearch = removeAccent(searchText);
    let maxScore = 0;

    for (const param of params) {
      let value = (object as any)[param.field];

      // Handle function values
      if (typeof value === 'function') {
        value = value.apply(object);
      }

      if (value && typeof value === 'string') {
        const normalizedValue = removeAccent(value);
        const idx = normalizedValue.indexOf(normalizedSearch);

        if (idx === 0) {
          maxScore = Math.max(maxScore, param.scoreBase + 0.7);
        } else if (idx > 0) {
          maxScore = Math.max(maxScore, param.scoreBase + 0.3);
        }
      }
    }

    return maxScore;
  };
}

function getPropertyByPath(obj: any, path: string): any {
  const parts = path.split('.');
  let current = obj;
  for (const part of parts) {
    if (current == null) return undefined;
    current = current[part];
  }
  return current;
}

/**
 * Applies a scorer to an array and returns sorted results
 */
function applyScorer<T>(
  array: T[],
  scorer: ScorerFunction<T>,
  searchtext: string
): T[] {
  const normalizedSearchText = normalize(searchtext);

  // Map to scored object, and remove score <= 0
  const searchResultScored: ScoredData<T>[] = array
    .map((data) => {
      return { data, score: scorer(data, normalizedSearchText) };
    })
    .filter((scoredData) => scoredData.score > 0);

  // Sort by score
  searchResultScored.sort((a, b) => b.score - a.score);

  // Return result
  return searchResultScored.map((scored) => scored.data);
}

function scoreString(
  property: string | null | undefined,
  scoreBase: number,
  normalizedSearchText: string
): number {
  const idx = property ? normalize(property).indexOf(normalizedSearchText) : -1;

  if (idx === 0) {
    return scoreBase + 5;
  } else if (idx > 0) {
    return scoreBase + 4;
  } else {
    return 0;
  }
}

function scoreArray(
  array: any[],
  scoreBaseRoot: ScoreBase,
  normalizedSearchText: string
): number {
  return array
    .map((arrayItem) => {
      if (typeof arrayItem === 'string') {
        return scoreString(arrayItem, scoreBaseRoot as number, normalizedSearchText);
      } else if (Array.isArray(arrayItem)) {
        return scoreArray(arrayItem, scoreBaseRoot, normalizedSearchText);
      } else {
        return scoreObject(arrayItem, scoreBaseRoot, normalizedSearchText);
      }
    })
    .reduce(max, 0);
}

function scoreObject(
  object: any,
  scoreBaseRoot: ScoreBase,
  normalizedSearchText: string
): number {
  if (typeof scoreBaseRoot === 'number') {
    return scoreBaseRoot;
  }

  return Object.entries(scoreBaseRoot)
    .map(([key, scoreBase]) => {
      const property = object[key];

      if (typeof scoreBase === 'number') {
        if (typeof property === 'string') {
          return scoreString(property, scoreBase, normalizedSearchText);
        } else if (Array.isArray(property)) {
          if (property.length === 0) return 0;
          return scoreArray(property, scoreBase, normalizedSearchText);
        } else {
          return 0;
        }
      } else if (typeof scoreBase === 'object') {
        if (property && typeof property === 'object') {
          return scoreObject(property, scoreBase, normalizedSearchText);
        } else {
          return 0;
        }
      }
      return 0;
    })
    .reduce(max, 0);
}